// src/pages/FundPlanner/components/Funds/useFileManager.js
import { useMemo, useCallback } from "react";
import useFileManager from "components/FileManager/hooks/useFileManager";
import { useProfile } from "context/ProfileContext";
import { getSharingPermissions } from "lib/permissions";

export function useInvestmentFundsFileManager({ limit = 100 } = {}) {
  const { profile, selectedOrg } = useProfile();

  const {
    records: rawRecords,
    recents,
    loading,
    error,
    fetchRecords,
    addToRecents,
  } = useFileManager({
    table: "fp_investment_funds",
    org: selectedOrg,
    limit,
    select: `
      id,
      org_id,
      tenant_id,
      created_by,
      name,
      description,
      fund_type,
      portfolio_id,
      sharing,
      created_at,
      updated_at,
      portfolio:fp_portfolios!fp_investment_funds_portfolio_id_fkey (
        id,
        name
      ),
      portfolio_memberships:fp_investment_fund_portfolios (
        portfolio:fp_portfolios (
          id,
          name
        )
      )
    `,
  });

  const records = useMemo(() => {
    if (!Array.isArray(rawRecords)) return [];

    return rawRecords.map((r) => ({
      ...r,
      ...getSharingPermissions(r, profile),
    }));
  }, [rawRecords, profile]);

  const openRecord = useCallback(
    (fund) => {
      addToRecents?.(fund);
    },
    [addToRecents]
  );

  const isReadOnlyRecord = useCallback(
    (r) => getSharingPermissions(r, profile).isReadOnly,
    [profile]
  );

  const hasAccessError = useMemo(() => {
    if (!selectedOrg) return false;
    if (loading || error) return false;
    return Array.isArray(records) && records.length === 0;
  }, [selectedOrg, loading, error, records]);

  return {
    selectedOrg,
    records,
    recents,
    loading,
    error,
    refresh: fetchRecords,
    openRecord,
    isReadOnlyRecord,
    hasAccessError,
    newLabel: "New Fund",
  };
}

function normStr(x) {
  return String(x ?? "").trim();
}

function getFundName(r) {
  return normStr(r?.name) || "(Untitled Fund)";
}

function getDescription(r) {
  return normStr(r?.description);
}

function getPortfolioGroupName(r) {
  const names = (r?.portfolio_memberships || [])
    .map((membership) => normStr(membership?.portfolio?.name))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return names.length ? names.join(", ") : normStr(r?.portfolio?.name);
}

function getFundTypeValue(r) {
  return normStr(r?.fund_type);
}

function getFundTypeLabel(r) {
  const value = getFundTypeValue(r);

  switch (value) {
    case "bond_proceeds":
      return "Bond Proceeds";
    case "capital_reserve_stabilization":
      return "Capital Reserve / Stabilization";
    case "debt_service":
      return "Debt Service";
    case "insurance_risk_reserve":
      return "Insurance / Risk Reserve";
    case "operating":
      return "Operating";
    case "program":
      return "Program";
    case "restricted_revenue":
      return "Restricted Revenue";
    case "other":
      return "Other";
    default:
      return value || "";
  }
}

function formatDateShort(value) {
  const s = String(value ?? "").trim();
  return s ? s.slice(0, 10) : "";
}

const COL_NAME = "w-[32%]";
const COL_PORTFOLIO = "w-[32%]";
const COL_TYPE = "w-[20%]";
const COL_SMALL = "w-[16%]";

export const investmentFundsOpenView = {
  title: "Open Fund",

  defaultSort: { key: "name", asc: true },

  search: {
    placeholder: "Search funds…",
    predicate: (r, q) => {
      const portfolioGroup = getPortfolioGroupName(r).toLowerCase();
      const name = getFundName(r).toLowerCase();
      const fundType = getFundTypeLabel(r).toLowerCase();
      const description = getDescription(r).toLowerCase();

      return (
        portfolioGroup.includes(q) ||
        name.includes(q) ||
        fundType.includes(q) ||
        description.includes(q)
      );
    },
  },

  selectedLabel: (r) => {
    const portfolioGroup = normStr(getPortfolioGroupName(r));
    const name = normStr(getFundName(r));
    if (portfolioGroup && name) return `${name} — ${portfolioGroup}`;
    return name || portfolioGroup || "";
  },

  columns: [
    {
      key: "name",
      header: "Fund",
      align: "left",
      headerClassName: COL_NAME,
      cellClassName: COL_NAME,
      sortValue: (r) => getFundName(r).toLowerCase(),
      showReadOnlyBadge: true,
      render: (r) => {
        const name = getFundName(r);
        return (
          <div className="truncate" title={name}>
            {name}
          </div>
        );
      },
    },
    {
      key: "portfolio_group",
      header: "Portfolio Group",
      align: "left",
      headerClassName: COL_PORTFOLIO,
      cellClassName: COL_PORTFOLIO,
      sortValue: (r) => getPortfolioGroupName(r).toLowerCase(),
      render: (r) => {
        const portfolioGroup = getPortfolioGroupName(r);
        return portfolioGroup ? (
          <div className="truncate" title={portfolioGroup}>
            {portfolioGroup}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "fund_type",
      header: "Fund Type",
      align: "left",
      headerClassName: COL_TYPE,
      cellClassName: COL_TYPE,
      sortValue: (r) => getFundTypeLabel(r).toLowerCase(),
      render: (r) => {
        const fundType = getFundTypeLabel(r);
        return fundType ? (
          <div className="truncate" title={fundType}>
            {fundType}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "updated_at",
      header: "Updated",
      align: "left",
      headerClassName: COL_SMALL,
      cellClassName: `${COL_SMALL} whitespace-nowrap tabular-nums`,
      defaultAsc: false,
      sortValue: (r) => normStr(r?.updated_at),
      render: (r) => {
        const shown = formatDateShort(r?.updated_at);
        return shown ? (
          <div className="truncate" title={shown}>
            {shown}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
  ],
};
